import { AudioManager } from './audioManager';
import { SettingsManager } from './settingsManager';

/**
 * Creates a styled button with medieval theme
 * @param text - The text to display on the button
 * @param onClick - The function to call when the button is clicked
 * @param subtle - Whether the button should be subtle
 * @returns HTMLButtonElement - The styled button element
 */
export function createMedievalButton(text: string, onClick: () => void, subtle: boolean = false): HTMLButtonElement {
    const button = document.createElement('button');
    button.textContent = text;
    
    // Apply appropriate class based on subtle flag
    // Hover effects are handled by CSS (.medieval-button:hover, .medieval-button-subtle:hover)
    button.className = subtle ? 'medieval-button-subtle' : 'medieval-button';

    // Add click handler
    button.addEventListener('click', () => {
        // Play click sound if AudioManager is initialized
        const audioManager = AudioManager.getInstance();
        if (audioManager.isInitialized) {
            audioManager.playSound(AudioManager.SOUNDS.Click);
        }
        // Call the provided onClick handler
        onClick();
    });
    
    return button;
}

/**
 * Creates a container for UI elements with consistent styling
 * @param position - The position of the container ('center' | 'top' | 'bottom')
 * @returns HTMLDivElement - The styled container element
 */
export function createUIContainer(position: 'center' | 'top' | 'bottom'): HTMLDivElement {
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '50%';
    container.style.transform = 'translateX(-50%)';
    container.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    container.style.padding = '40px';
    container.style.borderRadius = '10px';
    container.style.textAlign = 'center';
    container.style.display = 'none';
    container.style.zIndex = '1001';
    container.style.opacity = '0';
    container.style.transition = 'opacity 0.5s ease-in-out';
    
    // Position the container
    switch (position) {
        case 'center':
            container.style.top = '50%';
            container.style.transform = 'translate(-50%, -50%)';
            break;
        case 'top':
            container.style.top = '20px';
            break;
        case 'bottom':
            container.style.bottom = '20px';
            break;
    }
    
    return container;
}

/**
 * Creates an overlay for UI elements
 * @returns HTMLDivElement - The styled overlay element
 */
export function createOverlay(): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    overlay.style.display = 'none';
    overlay.style.zIndex = '1000';
    overlay.style.transition = 'opacity 0.5s ease-in-out';
    
    return overlay;
}

type TitleLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/**
 * Creates a title with medieval styling
 * @param text - The text to display
 * @returns HTMLHeadingElement - The styled title element
 */
export function createMedievalTitle(text: string, level: TitleLevel = 'h1'): HTMLHeadingElement {
    const title = document.createElement(level);
    title.textContent = text;
    title.style.color = '#ffd700';
    title.style.fontFamily = 'MedievalPixel, monospace';
    title.style.textShadow = '0 0 10px #ff6600, 0 0 20px #ff6600';
    title.style.letterSpacing = '4px';
    
    return title;
}

/**
 * Shows a UI container and its overlay with fade effect
 * @param container - The container to show
 * @param overlay - The overlay to show
 */
export function showUI(container: HTMLDivElement, overlay: HTMLDivElement): void {
    overlay.style.display = 'block';
    overlay.style.opacity = '1';
    
    setTimeout(() => {
        container.style.display = 'block';
        container.style.opacity = '1';
    }, 100);
}

/**
 * Hides a UI container and its overlay with fade effect
 * @param container - The container to hide
 * @param overlay - The overlay to hide
 */
export function hideUI(container: HTMLDivElement, overlay: HTMLDivElement): void {
    container.style.opacity = '0';
    
    setTimeout(() => {
        container.style.display = 'none';
        overlay.style.opacity = '0';
        setTimeout(() => {
            overlay.style.display = 'none';
        }, 500);
    }, 500);
}

/**
 * Creates a health bar with medieval styling
 * @param maxHealth - Maximum health value
 * @param currentHealth - Current health value
 * @returns HTMLDivElement - The container with the health bar
 */
export function createHealthBar(maxHealth: number, currentHealth: number): HTMLDivElement {
    
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.bottom = '30px';
    container.style.left = '30px';
    container.style.width = '300px';
    container.style.height = '64px';

    const healthBarContainer = document.createElement('div');
    healthBarContainer.style.position = 'absolute';
    healthBarContainer.style.bottom = '65px';
    healthBarContainer.style.left = '70px';
    healthBarContainer.style.width = '187px';
    healthBarContainer.style.height = '20px';

    // Simple health bar
    const healthBar = document.createElement('div');
    healthBar.style.width = '100%';
    healthBar.style.height = '16px';
    healthBar.style.left = '30px';
    healthBar.style.backgroundColor = 'rgba(0,0,0,0.5)';
    healthBar.style.marginTop = '24px';

    // Health fill
    const healthFill = document.createElement('div');
    healthFill.className = 'health-fill';
    healthFill.style.width = `${(currentHealth / maxHealth) * 100}%`;
    healthFill.style.height = '100%';
    healthFill.style.left = '30px';
    healthFill.style.backgroundColor = '#ff0000';
    healthFill.style.transition = 'width 0.3s ease-in-out';

    // damageFlash keyframes and .damage-flash class are defined in ui.css

    // Overlay image
    const overlay = document.createElement('img');
    overlay.src = 'healthbar.png';
    overlay.style.position = 'absolute';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '300px';
    overlay.style.height = 'auto';

    healthBarContainer.appendChild(healthBar);
    healthBarContainer.appendChild(healthFill);
    container.appendChild(healthBarContainer);
    container.appendChild(overlay);
    container.classList.add('combat-stats');
    
    return container;
}

/**
 * Updates the health bar with new values
 * @param container - The health bar container
 * @param maxHealth - Maximum health value
 * @param currentHealth - Current health value
 */
export function updateMedievalHealthBar(container: HTMLDivElement, maxHealth: number, currentHealth: number): void {
    const healthFill = container.querySelector('.health-fill') as HTMLDivElement;
    if (healthFill) {
        healthFill.style.width = `${(currentHealth / maxHealth) * 100}%`;
        // Add flash effect
        healthFill.classList.remove('damage-flash');
        // Force browser to reflow
        void healthFill.offsetWidth;
        healthFill.classList.add('damage-flash');
    }
}

/**
 * Creates a loading indicator with medieval styling
 * @returns HTMLDivElement - The loading indicator element
 */
export function createLoadingIndicator(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'loading-indicator';
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.backgroundColor = '#000';
    container.style.zIndex = '2000';
    container.style.display = 'none';

    const loadingText = document.createElement('div');
    loadingText.style.position = 'absolute';
    loadingText.style.top = '50%';
    loadingText.style.left = '50%';
    loadingText.style.transform = 'translate(-50%, -50%)';
    loadingText.style.color = '#ffd700';
    loadingText.style.fontFamily = 'MedievalPixel, monospace';
    loadingText.style.fontSize = '2vw';
    loadingText.style.textShadow = '0 0 10px #ff6600';
    loadingText.style.whiteSpace = 'nowrap';
    loadingText.textContent = 'Loading';

    const dots = document.createElement('span');
    dots.className = 'loading-dots';
    loadingText.appendChild(dots);

    // Store interval ID in the container's data
    const intervalId = setInterval(() => {
        const currentText = dots.textContent || '';
        dots.textContent = currentText.length >= 3 ? '' : currentText + '.';
    }, 200);
    container.dataset.intervalId = intervalId.toString();

    container.appendChild(loadingText);
    return container;
}

/**
 * Shows the loading indicator
 * @param container - The loading indicator container
 */
export function showLoadingIndicator(container: HTMLDivElement): void {
    container.style.display = 'block';
}

/**
 * Hides the loading indicator
 * @param container - The loading indicator container
 */
export function hideLoadingIndicator(container: HTMLDivElement): void {
    container.style.display = 'none';
    // Clear the interval when hiding
    const intervalId = parseInt(container.dataset.intervalId || '0');
    if (intervalId) {
        clearInterval(intervalId);
        container.dataset.intervalId = '';
    }
}

/**
 * Creates a medieval styled slider
 * @param min - Minimum value
 * @param max - Maximum value
 * @param value - Initial value
 * @param onChange - Function to call when slider value changes
 * @param disabled - Whether the slider should be disabled initially
 * @returns HTMLDivElement - Container with the styled slider
 */
export function createMedievalSlider(
    min: string | number,
    max: string | number, 
    value: string | number,
    onChange: (value: number) => void,
    disabled: boolean = false
): HTMLDivElement {
    // Create container
    const container = document.createElement('div');
    container.className = 'slider-container';
    
    // Create the slider
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.className = 'medieval-slider';
    slider.min = min.toString();
    slider.max = max.toString();
    slider.value = value.toString();
    slider.disabled = disabled;
    
    // Set initial percentage CSS variable for WebKit browsers
    const minVal = parseFloat(min.toString());
    const maxVal = parseFloat(max.toString());
    const initialVal = parseFloat(value.toString());
    const percentage = ((initialVal - minVal) / (maxVal - minVal)) * 100;
    slider.style.setProperty('--slider-percentage', `${percentage}%`);
    
    // Update value and CSS variable when slider changes
    slider.addEventListener('input', () => {
        const newValue = parseFloat(slider.value);
        const newPercentage = ((newValue - minVal) / (maxVal - minVal)) * 100;
        slider.style.setProperty('--slider-percentage', `${newPercentage}%`);
        onChange(newValue);
    });
    
    // Add elements to container
    container.appendChild(slider);
    
    return container;
}

function createDialogInternal(id: string, title: string, width?: string | undefined): HTMLDivElement {
    const dialog = document.createElement('div');
    dialog.id = id;
    dialog.className = 'ui-container menu-container';
    dialog.style.zIndex = '1005';
    dialog.style.display = 'none';
    if (width) {
        dialog.style.width = width;
    }
    dialog.style.position = 'fixed';
    dialog.style.top = '50%';
    dialog.style.left = '50%';
    dialog.style.transform = 'translate(-50%, -50%)';
    dialog.style.opacity = '0';
    dialog.style.transition = 'opacity 0.3s ease-in-out';

    const dialogTitle = createMedievalTitle(title, 'h1');
    dialogTitle.style.marginTop = '0';

    dialog.appendChild(dialogTitle);
    dialog.appendChild(createDivider());
    return dialog;
}
export function createCreditsDialog(): HTMLDivElement {
    let dialog = createDialogInternal('credits-dialog', 'Credits', '500px');

    // Create credits content
    const creditsContent = document.createElement('div');
    creditsContent.className = 'settings-group';

    // Music credits section
    const musicTitle = createTitle('Music', 'h2');
    creditsContent.appendChild(musicTitle);

    const menuMusicTitle = createTitle('Menu Music', 'h4');
    creditsContent.appendChild(menuMusicTitle);
    const menuBandcampLink = createLink('[Bandcamp]', 'https://cryocrypt.bandcamp.com/album/sorgeberget');
    const menuMusicCredit = createParagraph('Nordanvinden by Sorgeberget ');
    menuMusicCredit.appendChild(menuBandcampLink);
    creditsContent.appendChild(menuMusicCredit);

    const gameMusicTitle = createTitle('Game Background Music', 'h4');
    creditsContent.appendChild(gameMusicTitle);
    const gameBandcampLink = createLink('[Bandcamp]', 'https://cryocrypt.bandcamp.com/album/frostfall');
    const gameMusicCredit = createParagraph('Dungeon Stairs by Mountain Realm ');
    gameMusicCredit.appendChild(gameBandcampLink);
    creditsContent.appendChild(gameMusicCredit);

    // Models group
    creditsContent.appendChild(createDivider());
    const modelsTitle = createTitle('Models', 'h2');
    creditsContent.appendChild(modelsTitle);

    const playerCharacterTitle = createTitle('Player Character', 'h4');
    creditsContent.appendChild(playerCharacterTitle);
    const playerPolyLink = createLink('Hooded Adventurer by Quaternius', 'https://poly.pizza/m/y9KWOVG21R');
    const playerCharacterModel = createParagraph('');
    playerCharacterModel.appendChild(playerPolyLink);
    creditsContent.appendChild(playerCharacterModel);

    // Skeleton
    const skeletonTitle = createTitle('Skeleton', 'h4');
    creditsContent.appendChild(skeletonTitle);
    const skeletonPolyLink = createLink('Skeleton by Quaternius', 'https://poly.pizza/m/yq5ATpujSt');
    const skeletonModel = createParagraph('');
    skeletonModel.appendChild(skeletonPolyLink);
    creditsContent.appendChild(skeletonModel);

    const healthPotionTitle = createTitle('Health Potion', 'h4');
    creditsContent.appendChild(healthPotionTitle);
    const ccLink = createLink('[CC-BY]', 'https://creativecommons.org/licenses/by/3.0/');
    const potionPolyLink = createLink('Poly Pizza', 'https://poly.pizza/m/5DvOJDIgDYu');
    const healthPotionModel = createParagraph('Complex Health Potion by Garrett LeFever ');
    healthPotionModel.appendChild(ccLink);
    healthPotionModel.appendChild(document.createTextNode(' via '));
    healthPotionModel.appendChild(potionPolyLink);
    creditsContent.appendChild(healthPotionModel);

    dialog.appendChild(creditsContent);
    dialog.appendChild(createDivider());

    const closeButtonContainer = document.createElement('div');
    closeButtonContainer.id = 'credits-close-button-container';

    // Create close button
    const closeButton = createMedievalButton('Close', () => {
        dialog.style.opacity = '0'; 
        setTimeout(() => {
            dialog.style.display = 'none';
        }, 300);
    });
    closeButtonContainer.appendChild(closeButton);

    dialog.appendChild(closeButtonContainer);
    return dialog;
}

function createDivider(): HTMLHRElement {
    const hr = document.createElement('hr');
    hr.className = 'dialog-divider';
    return hr;
}

function createLink(text: string, url: string): HTMLAnchorElement {
    const link = document.createElement('a');
    link.textContent = text;
    link.href = url;
    link.target = '_blank';
    link.className = 'medieval-link';
    return link;
}

function createTitle(text: string, level: TitleLevel = 'h3'): HTMLHeadingElement {
    const title = document.createElement(level);
    title.textContent = text;
    title.style.color = '#ffd700';
    title.style.fontFamily = "'Roboto Slab', serif";
    
    // Add appropriate spacing based on heading level
    if (level === 'h1') {
        title.style.marginTop = '20px';
        title.style.marginBottom = '25px';
    } else if (level === 'h2') {
        title.style.marginTop = '30px';
        title.style.marginBottom = '15px';
    } else if (level === 'h3') {
        title.style.marginTop = '20px';
        title.style.marginBottom = '10px';
    } else if (level === 'h4') {
        title.style.marginTop = '20px';
        title.style.marginBottom = '8px';
    } else {
        title.style.marginTop = '15px';
        title.style.marginBottom = '5px';
    }
    
    return title;
}

function createParagraph(text: string): HTMLParagraphElement {
    const paragraph = document.createElement('p');
    paragraph.textContent = text;
    paragraph.style.fontSize = '14px';
    paragraph.style.marginLeft = '5px';
    paragraph.style.marginBottom = '15px';
    paragraph.style.color = '#ffd700';
    paragraph.style.fontFamily = "'Roboto Slab', serif";
    return paragraph;
}

/**
 * Shows the credits dialog
 * @param dialog - The credits dialog element
 */
export function showCreditsDialog(dialog: HTMLDivElement): void {
    dialog.style.display = 'block';
    setTimeout(() => {
        dialog.style.opacity = '1';
    }, 10);
}
/**
 * Hides the settings dialog
 * @param dialog - The settings dialog element
 */
export function hideCreditsDialog(dialog: HTMLDivElement): void {
    dialog.style.opacity = '0';
    setTimeout(() => {
        dialog.style.display = 'none';
    }, 300);
}
/**
 * Creates a settings dialog with audio controls
 * @returns HTMLDivElement - The settings dialog element
 */
export function createSettingsDialog(): HTMLDivElement {
    // Create container
    const dialog = createDialogInternal('settings-dialog', 'Settings', '500px');

    // Get manager instances
    const audioManager = AudioManager.getInstance();
    const settingsManager = SettingsManager.getInstance();
    const settings = settingsManager.getSettings();

    // Create sound effects enable group
    const soundEffectsEnableGroup = document.createElement('div');
    soundEffectsEnableGroup.className = 'settings-group';

    // Create sound effects volume group
    const soundEffectsVolumeGroup = document.createElement('div');
    soundEffectsVolumeGroup.className = 'settings-group';

    // Create background music enable group
    const backgroundMusicEnableGroup = document.createElement('div');
    backgroundMusicEnableGroup.className = 'settings-group';

    // Create background music volume group
    const backgroundMusicVolumeGroup = document.createElement('div');
    backgroundMusicVolumeGroup.className = 'settings-group';

    // Create cookies consent group
    const cookiesConsentGroup = document.createElement('div');
    cookiesConsentGroup.className = 'settings-group';

    // Create settings controls for sound effects
    const soundEffectsLabel = document.createElement('label');
    soundEffectsLabel.textContent = 'Enable Sound';
    soundEffectsLabel.style.color = '#ffd700';
    soundEffectsLabel.style.fontFamily = "'Roboto Slab', serif";
    soundEffectsLabel.style.display = 'block';
    soundEffectsLabel.style.marginBottom = '10px';

    const soundEffectsCheckbox = document.createElement('input');
    soundEffectsCheckbox.className = 'settings-checkbox';
    soundEffectsCheckbox.type = 'checkbox';
    soundEffectsCheckbox.id = 'sound-effects-enabled';
    soundEffectsCheckbox.checked = audioManager.isEnabled();
    soundEffectsCheckbox.style.marginRight = '10px';

    const soundEffectsSliderLabel = document.createElement('label');
    soundEffectsSliderLabel.textContent = 'Sound Effects Volume';
    soundEffectsSliderLabel.style.color = '#ffd700';
    soundEffectsSliderLabel.style.fontFamily = "'Roboto Slab', serif";
    soundEffectsSliderLabel.style.display = 'block';
    soundEffectsSliderLabel.style.marginBottom = '10px';

    // Create settings controls for background music
    const backgroundMusicLabel = document.createElement('label');
    backgroundMusicLabel.textContent = 'Enable Background Music';
    backgroundMusicLabel.style.color = '#ffd700';
    backgroundMusicLabel.style.fontFamily = "'Roboto Slab', serif";
    backgroundMusicLabel.style.display = 'block';
    backgroundMusicLabel.style.marginBottom = '10px';

    const backgroundMusicCheckbox = document.createElement('input');
    backgroundMusicCheckbox.className = 'settings-checkbox';
    backgroundMusicCheckbox.type = 'checkbox';
    backgroundMusicCheckbox.id = 'background-music-enabled';
    backgroundMusicCheckbox.checked = audioManager.isMusicEnabled();
    backgroundMusicCheckbox.style.marginRight = '10px';

    const backgroundMusicSliderLabel = document.createElement('label');
    backgroundMusicSliderLabel.textContent = 'Background Music Volume';
    backgroundMusicSliderLabel.style.color = '#ffd700';
    backgroundMusicSliderLabel.style.fontFamily = "'Roboto Slab', serif";
    backgroundMusicSliderLabel.style.display = 'block';
    backgroundMusicSliderLabel.style.marginBottom = '10px';

    // Create cookies consent controls
    const cookiesConsentLabel = document.createElement('label');
    cookiesConsentLabel.textContent = 'Allow Cookies & Storage';
    cookiesConsentLabel.style.color = '#ffd700';
    cookiesConsentLabel.style.fontFamily = "'Roboto Slab', serif";
    cookiesConsentLabel.style.display = 'block';
    cookiesConsentLabel.style.marginBottom = '10px';

    const cookiesConsentCheckbox = document.createElement('input');
    cookiesConsentCheckbox.className = 'settings-checkbox';
    cookiesConsentCheckbox.type = 'checkbox';
    cookiesConsentCheckbox.id = 'cookies-consent';
    cookiesConsentCheckbox.checked = settings.cookies.allowStorage;
    cookiesConsentCheckbox.style.marginRight = '10px';

    const cookiesConsentDescription = createParagraph('We use cookies to save your preferences. These cookies help us remember your settings when you return to the game.');
    
    // Create volume slider for sound effects
    const soundEffectsSliderContainer = createMedievalSlider(
        0,
        100,
        audioManager.getVolume() * 100,
        (value) => {
            audioManager.setVolume(value / 100);
        },
        !soundEffectsCheckbox.checked
    );

    // Create volume slider for background music
    const backgroundMusicSliderContainer = createMedievalSlider(
        0,
        100,
        audioManager.getMusicVolume() * 100,
        (value) => {
            audioManager.setMusicVolume(value / 100);
        },
        !backgroundMusicCheckbox.checked
    );

    // Get the actual slider input elements
    const soundEffectsSlider = soundEffectsSliderContainer.querySelector('input') as HTMLInputElement;
    const backgroundMusicSlider = backgroundMusicSliderContainer.querySelector('input') as HTMLInputElement;

    // Add event listeners for sound effects checkbox (master control)
    soundEffectsCheckbox.addEventListener('change', () => {
        audioManager.setEnabled(soundEffectsCheckbox.checked);
        
        // Update sound effects slider
        if (soundEffectsSlider) {
            soundEffectsSlider.disabled = !soundEffectsCheckbox.checked;
        }
        
        // If master sound is disabled, also disable background music controls
        if (!soundEffectsCheckbox.checked) {
            if (backgroundMusicCheckbox) {
                backgroundMusicCheckbox.disabled = true;
            }
            if (backgroundMusicSlider) {
                backgroundMusicSlider.disabled = true;
            }
        } else {
            // Re-enable background music controls
            if (backgroundMusicCheckbox) {
                backgroundMusicCheckbox.disabled = false;
            }
            if (backgroundMusicSlider) {
                backgroundMusicSlider.disabled = !backgroundMusicCheckbox.checked;
            }
        }
    });

    // Add event listeners for background music checkbox
    backgroundMusicCheckbox.addEventListener('change', () => {
        // Determine if we're in the menu or game based on whether the game container is visible
        const isInGame = document.getElementById('game-container')?.style.display === 'block';
        const context = isInGame ? 'game' : 'menu';
        
        audioManager.setMusicEnabled(backgroundMusicCheckbox.checked, context);
        if (backgroundMusicSlider) {
            backgroundMusicSlider.disabled = !backgroundMusicCheckbox.checked;
        }
    });

    // Add event listener for cookies consent checkbox
    cookiesConsentCheckbox.addEventListener('change', () => {
        settingsManager.setCookieConsent(cookiesConsentCheckbox.checked);
    });

    // Add elements to their groups
    soundEffectsLabel.insertBefore(soundEffectsCheckbox, soundEffectsLabel.firstChild);
    soundEffectsEnableGroup.appendChild(soundEffectsLabel);
    
    soundEffectsVolumeGroup.appendChild(soundEffectsSliderLabel);
    soundEffectsVolumeGroup.appendChild(soundEffectsSliderContainer);

    backgroundMusicLabel.insertBefore(backgroundMusicCheckbox, backgroundMusicLabel.firstChild);
    backgroundMusicEnableGroup.appendChild(backgroundMusicLabel);
    
    backgroundMusicVolumeGroup.appendChild(backgroundMusicSliderLabel);
    backgroundMusicVolumeGroup.appendChild(backgroundMusicSliderContainer);

    cookiesConsentLabel.insertBefore(cookiesConsentCheckbox, cookiesConsentLabel.firstChild);
    cookiesConsentGroup.appendChild(cookiesConsentLabel);
    cookiesConsentGroup.appendChild(cookiesConsentDescription);

    const closeButtonContainer = document.createElement('div');
    closeButtonContainer.id = 'settings-close-button-container';

    // Create close button
    const closeButton = createMedievalButton('Close', () => {
        dialog.style.opacity = '0';
        setTimeout(() => {
            dialog.style.display = 'none';
        }, 300);
    });
    closeButtonContainer.appendChild(closeButton);
    
    // Add a header for audio settings
    const audioHeader = createTitle('Audio', 'h2');
    dialog.appendChild(audioHeader);

    dialog.appendChild(soundEffectsEnableGroup);
    dialog.appendChild(soundEffectsVolumeGroup);
    dialog.appendChild(backgroundMusicEnableGroup);
    dialog.appendChild(backgroundMusicVolumeGroup);

    // Decorative divider
    dialog.appendChild(createDivider());

    // Add a header for privacy settings
    const privacyHeader = createTitle('Privacy', 'h2');
    dialog.appendChild(privacyHeader);

    dialog.appendChild(cookiesConsentGroup);
    dialog.appendChild(createDivider());
    dialog.appendChild(closeButtonContainer);

    return dialog;
}

/**
 * Shows the settings dialog
 * @param dialog - The settings dialog element
 */
export function showSettingsDialog(dialog: HTMLDivElement): void {
    dialog.style.display = 'block';
    setTimeout(() => {
        dialog.style.opacity = '1';
    }, 10);
}

/**
 * Hides the settings dialog
 * @param dialog - The settings dialog element
 */
export function hideSettingsDialog(dialog: HTMLDivElement): void {
    dialog.style.opacity = '0';
    setTimeout(() => {
        dialog.style.display = 'none';
    }, 300);
}

/**
 * Creates a game menu with resume, restart, and quit options
 * @param onResume - Callback for resume button
 * @param onRestart - Callback for restart button
 * @param onSettings - Callback for quit button
 * @returns Object containing menu container and overlay
 */
export function createGameMenu(
    onResume: () => void,
    onRestart: () => void,
    onSettings: () => void
): { container: HTMLDivElement, overlay: HTMLDivElement } {
    const menuContainer = document.createElement('div');
    menuContainer.className = 'ui-container menu-container';
    menuContainer.style.display = 'none';
    menuContainer.style.position = 'fixed';
    menuContainer.style.top = '50%';
    menuContainer.style.left = '50%';
    menuContainer.style.transform = 'translate(-50%, -50%)';
    menuContainer.style.opacity = '0';
    menuContainer.style.transition = 'opacity 0.3s ease-in-out';
    
    // Create title
    const title = createMedievalTitle('Game Paused');
    title.style.marginTop = '0';
    const titleDivider = createDivider();

    // Create buttons
    const resumeButton = createMedievalButton('Resume Game', onResume);
    const restartButton = createMedievalButton('Restart Game', onRestart);
    const settingsButton = createMedievalButton('Settings', onSettings);
    
    // Create overlay
    const menuOverlay = createOverlay();
    menuOverlay.id = 'game-menu-overlay';
    
    // Add elements to menu
    menuContainer.appendChild(title);
    menuContainer.appendChild(titleDivider);
    menuContainer.appendChild(resumeButton);
    menuContainer.appendChild(restartButton);
    menuContainer.appendChild(settingsButton);
    
    return { container: menuContainer, overlay: menuOverlay };
}

/**
 * Shows the game menu
 * @param container - The menu container
 * @param overlay - The menu overlay
 */
export function showGameMenu(container: HTMLDivElement, overlay: HTMLDivElement): void {
    showUI(container, overlay);
}

/**
 * Hides the game menu
 * @param container - The menu container
 * @param overlay - The menu overlay
 */
export function hideGameMenu(container: HTMLDivElement, overlay: HTMLDivElement): void {
    hideUI(container, overlay);
}

/**
 * Creates a cookie consent dialog for the welcome screen
 * @param onAccept - Callback for when cookies are accepted
 * @returns Object containing dialog container and overlay
 */
export function createCookieConsentDialog(onAccept: () => void): { container: HTMLDivElement, overlay: HTMLDivElement } {
    // Create overlay with semi-transparent background
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    overlay.style.zIndex = '1200';
    overlay.style.display = 'flex';
    overlay.style.justifyContent = 'flex-start';
    overlay.style.alignItems = 'flex-end';
    overlay.style.padding = '30px';
    
    // Create the dialog container
    const container = document.createElement('div');
    container.style.background = 'linear-gradient(180deg, rgba(20, 5, 5, 0.98) 0%, rgba(0, 0, 0, 0.98) 100%)';
    container.style.color = '#ffd700';
    container.style.border = '2px solid #ffd700';
    container.style.outline = '1px solid rgba(255, 215, 0, 0.2)';
    container.style.outlineOffset = '3px';
    container.style.borderRadius = '10px';
    container.style.padding = '25px';
    container.style.marginBottom = '50px';
    container.style.maxWidth = '400px';
    container.style.boxShadow = '0 0 40px rgba(0, 0, 0, 0.8), 0 0 15px rgba(255, 102, 0, 0.15)';
    
    // Create title
    const title = document.createElement('h2');
    title.textContent = 'Cookie Consent';
    title.style.fontFamily = 'MedievalPixel, monospace';
    title.style.color = '#ffd700';
    title.style.marginTop = '0';
    title.style.marginBottom = '15px';
    title.style.fontSize = '22px';
    
    // Create description
    const description = document.createElement('p');
    description.textContent = 'This game uses cookies to save your settings and preferences. We respect your privacy and only store what is necessary for the game to function properly.';
    description.style.marginBottom = '20px';
    description.style.lineHeight = '1.5';
    description.style.color = '#ffd700';
    description.style.fontFamily = "'Roboto Slab', serif";
    
    // Create button container
    const buttonContainer = document.createElement('div');
    buttonContainer.style.display = 'flex';
    buttonContainer.style.justifyContent = 'center';
    
    // Create accept button using medieval button style
    const acceptButton = createMedievalButton('Accept', () => {
        // Get settings manager and set consent
        const settingsManager = SettingsManager.getInstance();
        settingsManager.setCookieConsent(true);
        
        // Call the provided callback
        onAccept();
        
        // Hide overlay and dialog
        overlay.style.opacity = '0';
        setTimeout(() => {
            overlay.style.display = 'none';
        }, 300);
    });
    
    // Assemble the dialog
    buttonContainer.appendChild(acceptButton);
    container.appendChild(title);
    container.appendChild(description);
    container.appendChild(buttonContainer);
    overlay.appendChild(container);
    
    // Set transition for smooth fade effect
    overlay.style.transition = 'opacity 0.3s ease-in-out';
    
    return { container, overlay };
} 